import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X, User, Globe, ChevronDown, Heart, ShoppingBag, ShieldCheck, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useLang } from "../context/LanguageContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import { CODE_TO_URL, swapLocale } from "../lib/i18nRouter";
import { POLICY_LINKS } from "../pages/Policies";
import HeaderSearch from "./HeaderSearch";

const Switcher = ({ value, options, onChange, icon, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button data-testid={testid} onClick={() => setOpen(!open)} className="flex items-center gap-1.5 font-accent text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-gold transition-colors">
        {icon}
        <span>{value}</span>
        <ChevronDown size={11} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-3 bg-black/95 border border-white/15 min-w-[120px] z-50">
          {options.map((o) => (
            <button
              key={o.value || o}
              data-testid={`${testid}-option-${o.value || o}`}
              onClick={() => { onChange(o.value || o); setOpen(false); }}
              className="block w-full text-left px-4 py-3 font-accent text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-gold hover:bg-white/5"
            >
              {o.label || o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Desktop "Trust & Policies" mega-dropdown.
// Click to toggle; closes on outside-click or after navigation.
const TrustMenu = ({ lp, currentPath }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = currentPath.includes("/policies/");

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="nav-trust-policies"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 font-accent text-[11px] tracking-[0.24em] uppercase transition-colors duration-500 ${active ? "text-gold" : "text-white/60 hover:text-white"}`}
      >
        <ShieldCheck size={12} className="opacity-70" />
        <span>Trust & Policies</span>
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          data-testid="trust-policies-menu"
          className="absolute right-0 top-full mt-4 bg-black/95 backdrop-blur-2xl border border-gold/30 min-w-[260px] z-50 shadow-2xl"
        >
          <div className="px-5 pt-4 pb-2 border-b border-white/10">
            <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">Trust & Policies</p>
            <p className="text-white/40 text-[10px] font-light mt-1">Guarantees & house rules</p>
          </div>
          <ul className="py-2">
            {POLICY_LINKS.map((p) => (
              <li key={p.key}>
                <Link
                  to={lp(`/${p.path}`)}
                  onClick={() => setOpen(false)}
                  data-testid={`trust-policies-link-${p.key}`}
                  className="block px-5 py-2.5 font-accent text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-gold hover:bg-white/5 transition-colors"
                >
                  {p.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const { user, logout } = useAuth();
  const { currency, change: setCurrency, supported: curSupported } = useCurrency();
  const { lang, t, supported: langSupported } = useLang();
  const wl = useWishlist();
  const cart = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Locale-aware path builder
  const currentSlug = CODE_TO_URL[lang] || "en";
  const lp = (path) => `/${currentSlug}${path === "/" ? "" : path}`;

  // When user picks a language from the dropdown, swap the URL prefix
  // and let LocaleSync (in App.js) sync the context.
  const switchLang = (newCode) => {
    const slug = CODE_TO_URL[newCode] || "en";
    const next = swapLocale(location.pathname, slug);
    navigate(`${next}${location.search}${location.hash}`);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV = [
    { to: lp("/find-your-stone"), key: "find-your-stone", label: "Find Your Stone" },
    { to: lp("/collection"), key: "collection", label: t("nav_collection") },
    { to: lp("/auctions"), key: "auctions", label: t("nav_auctions") },
    { to: lp("/verify"), key: "verify", label: t("nav_verify") },
    { to: lp("/concierge"), key: "concierge", label: t("nav_concierge") },
    { to: lp("/about"), key: "about", label: t("nav_heritage") },
  ];

  return (
    <header data-testid="site-header" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-black/85 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"}`}>
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-12 py-6 md:py-7 flex items-center justify-between gap-6">
        <Link to={lp("/")} data-testid="logo-link" className="flex items-center shrink-0" aria-label="OPIKJEWELS — home">
          <picture>
            <source srcSet="/opikjewels-logo.webp" type="image/webp" />
            <img
              src="/opikjewels-logo.png"
              alt="OPIKJEWELS"
              className="block h-[45px] md:h-[70px] w-auto select-none"
              style={{ filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.35))" }}
              draggable="false"
              width="1024"
              height="671"
            />
          </picture>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {NAV.map((n) => (
            <NavLink
              key={n.key} to={n.to}
              data-testid={`nav-${n.key}`}
              className={({ isActive }) =>
                `font-accent text-[11px] tracking-[0.24em] uppercase transition-colors duration-500 ${isActive ? "text-gold" : "text-white/60 hover:text-white"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <TrustMenu lp={lp} currentPath={location.pathname} />
        </nav>

        <div className="hidden xl:flex items-center gap-4">
          <HeaderSearch />
          <Switcher
            testid="currency-switcher"
            value={currency}
            options={curSupported.map((c) => ({ value: c, label: c }))}
            onChange={setCurrency}
          />
          <Switcher
            testid="language-switcher"
            value={lang}
            options={langSupported.map((c) => ({
              value: c.value,
              label: <span className="flex items-center gap-2"><span className="text-sm leading-none">{c.flag}</span><span>{c.label}</span></span>,
            }))}
            onChange={switchLang}
            icon={<span className="text-sm leading-none">{(langSupported.find((s) => s.value === lang) || {}).flag || "🌐"}</span>}
            valueDisplay={<span>{lang}</span>}
          />
          <Link to={lp("/wishlist")} data-testid="wishlist-link" aria-label="Wishlist" className="relative text-white/70 hover:text-gold transition-colors flex items-center">
            <Heart size={14} />
            {wl?.count > 0 && (
              <span className="absolute -top-2 -right-3 bg-gold text-black font-accent text-[9px] tracking-[0.1em] px-1.5 py-0.5">{wl.count}</span>
            )}
          </Link>
          <Link to={lp("/cart")} data-testid="cart-link" aria-label="Cart" className="relative text-white/70 hover:text-gold transition-colors flex items-center">
            <ShoppingBag size={14} />
            {cart?.count > 0 && (
              <span data-testid="cart-badge" className="absolute -top-2 -right-3 bg-gold text-black font-accent text-[9px] tracking-[0.1em] px-1.5 py-0.5">{cart.count}</span>
            )}
          </Link>
          {user && user !== false ? (
            <>
              {user.role === "admin" && <Link to={lp("/admin")} data-testid="admin-link" className="font-accent text-[11px] tracking-[0.24em] uppercase text-gold hover:text-ivory">{t("nav_admin")}</Link>}
              {user.kyc_status !== "approved" && user.role !== "admin" && (
                <Link to={lp("/kyc")} data-testid="kyc-link" className="font-accent text-[11px] tracking-[0.24em] uppercase text-yellow-400 hover:text-yellow-300">
                  {user.kyc_status === "pending" ? "KYC ◷" : "Verify"}
                </Link>
              )}
              <button data-testid="logout-btn" onClick={async () => { await logout(); navigate(lp("/")); }} className="font-accent text-[11px] tracking-[0.24em] uppercase text-white/60 hover:text-white">{t("nav_signout")}</button>
            </>
          ) : (
            <Link to={lp("/login")} data-testid="signin-link" className="flex items-center gap-2 font-accent text-[11px] tracking-[0.24em] uppercase text-white/60 hover:text-gold transition-colors">
              <User size={14} /> {t("nav_signin")}
            </Link>
          )}
        </div>

        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="xl:hidden text-white" aria-label="Toggle menu">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-black/95 border-t border-white/10 px-6 py-8 space-y-5">
          {/* Mobile search — submits to /collection?q=… (luxury-styled inline form) */}
          <form
            data-testid="mobile-search-form"
            onSubmit={(e) => {
              e.preventDefault();
              const q = e.currentTarget.elements.q.value.trim();
              setOpen(false);
              navigate(lp(`/collection${q ? `?q=${encodeURIComponent(q)}` : ""}`));
            }}
            className="flex items-center gap-2 border border-gold/40 bg-black px-3 py-2"
          >
            <Search size={14} className="text-gold flex-shrink-0" />
            <input
              data-testid="mobile-search-input"
              type="search"
              name="q"
              autoComplete="off"
              placeholder="Search rubies, sapphires, origins…"
              className="bg-transparent text-ivory text-sm flex-1 outline-none placeholder:text-white/30"
            />
            <button
              type="submit"
              data-testid="mobile-search-submit"
              className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold hover:text-ivory"
            >
              Go
            </button>
          </form>

          {NAV.map((n) => (
            <Link key={n.key} to={n.to} onClick={() => setOpen(false)} data-testid={`mnav-${n.key}`} className="block font-accent text-[12px] tracking-[0.24em] uppercase text-white/70">{n.label}</Link>
          ))}

          <div className="pt-3 mt-3 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={12} className="text-gold opacity-80" />
              <span className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold">Trust & Policies</span>
            </div>
            <div className="grid grid-cols-2 gap-y-2 gap-x-4">
              {POLICY_LINKS.map((p) => (
                <Link
                  key={p.key}
                  to={lp(`/${p.path}`)}
                  onClick={() => setOpen(false)}
                  data-testid={`mnav-policy-${p.key}`}
                  className="block font-accent text-[11px] tracking-[0.22em] uppercase text-white/60 hover:text-gold"
                >
                  {p.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="flex gap-6 pt-4 border-t border-white/10">
            <Switcher testid="m-currency" value={currency} options={curSupported.map((c) => ({ value: c, label: c }))} onChange={setCurrency} />
            <Switcher testid="m-language" value={lang} options={langSupported.map((c) => ({ value: c.value, label: c.label }))} onChange={switchLang} icon={<Globe size={12} />} />
          </div>
          {user && user !== false ? (
            <>
              {user.role === "admin" && <Link to={lp("/admin")} onClick={() => setOpen(false)} className="block font-accent text-[12px] tracking-[0.24em] uppercase text-gold">{t("nav_admin")}</Link>}
              <button onClick={async () => { await logout(); setOpen(false); navigate(lp("/")); }} className="block font-accent text-[12px] tracking-[0.24em] uppercase text-white/70">{t("nav_signout")}</button>
            </>
          ) : (
            <Link to={lp("/login")} onClick={() => setOpen(false)} className="block font-accent text-[12px] tracking-[0.24em] uppercase text-gold">{t("nav_signin")}</Link>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
